import { Box, Button, CircularProgress, Typography } from "@mui/material";
import Image from "next/image";
import React from "react";
import { useCurrencyCode } from "../genericComponents/helper-functions/CurrencySymbol";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { AddToCartService } from "@/app/services/cart";
import { usePathname, useRouter } from "next/navigation";
import { Loader } from "../genericComponents/MainLoader/Loader";

interface ServiceItem {
  id: number;
  cid: number;
  name: string;
  subHeading: string;
  amount: number;
  mrp: number;
  discount: number;
  coverImage: { secureHttpPath: string };
}

export const VideoServices = ({
  completeVideo,
  cid,
}: {
  completeVideo: { servicesList: ServiceItem[] };
  cid: number;
}) => {
  const [clickedItems, setClickedItems] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [isCartAvailable, setIsCartAvailable] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const currencyCode = useCurrencyCode();
  const addToCartService = React.useMemo(() => new AddToCartService(), []);
  const router = useRouter();
  const pathName = usePathname();

  React.useEffect(() => {
    setIsCartAvailable(
      Object.values(clickedItems).some((value) => value === true)
    );
  }, [clickedItems]);

  const handleAddService = async (item: any, index: any) => {
    setIsLoading(true);
    const {
      id,
      cid,
      coverImage,
      aboutList,
      subHeading,
      serviceCharge,
      productCost,
      min,
      specialPrice,
      specialMessage,
    } = item;
    const imageUrl = coverImage ? coverImage.secureHttpPath : "";
    let countJson = await addToCartService.addToCart(
      id,
      true,
      cid,
      imageUrl,
      aboutList,
      subHeading,
      serviceCharge,
      productCost,
      min,
      specialPrice,
      specialMessage
    );

    if (countJson?.count && countJson?.count > 0) {
      setClickedItems((prev) => ({ ...prev, [id]: true }));
      setIsLoading(false);
    } else {
      setClickedItems((prev) => ({ ...prev, [id]: false }));
      setIsLoading(false);
    }
  };

  const handleRemoveService = async (item: any, index: any) => {
    setIsLoading(true);
    const {
      id,
      cid,
      coverImage,
      aboutList,
      subHeading,
      serviceCharge,
      productCost,
      min,
      specialPrice,
      specialMessage,
    } = item;
    const imageUrl = coverImage ? coverImage.secureHttpPath : "";
    let countJson = await addToCartService.addToCart(
      id,
      false,
      cid,
      imageUrl,
      aboutList,
      subHeading,
      serviceCharge,
      productCost,
      min,
      specialPrice,
      specialMessage
    );

    if (countJson?.count && countJson?.count > 0) {
      setClickedItems((prev) => ({ ...prev, [id]: true }));
      setIsLoading(false);
    } else {
      setClickedItems((prev) => ({ ...prev, [id]: false }));
      setIsLoading(false);
    }
  };

  const handleCartNavigate = (e: any) => {
    e.preventDefault(e);
    localStorage.setItem("cid", btoa(JSON.stringify(cid)));
    if (pathName !== "/cart") {
      setLoading(true);
      router.push("/cart");
    }
  };

  React.useEffect(() => {
    if (completeVideo && completeVideo?.servicesList?.length > 0) {
      completeVideo.servicesList.forEach((item) => {
        const { id, cid } = item;
        let jsonCount = addToCartService.checkIfAvailable(id, cid);

        if (jsonCount.count > 0) {
          setClickedItems((prev) => ({ ...prev, [id]: true }));
        } else {
          setClickedItems((prev) => ({ ...prev, [id]: false }));
        }
      });
    }
  }, [addToCartService, completeVideo]);

  return (
    <>
      {loading && <Loader />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          width: "100%",
          maxWidth: "100%",
          overflow: "scroll",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {completeVideo &&
          completeVideo?.servicesList?.map((item: any, index: any) => {
            const isClicked = clickedItems[item?.id] || false;

            return (
              <Box
                sx={{
                  background: "#fff",
                  padding: "0.5rem",
                  height: "7rem",
                  width: "18rem",
                  minWidth: "18rem",
                  maxWidth: "18rem",
                  display: "flex",
                  margin: !isCartAvailable ? "0.5rem" : "auto 0.5rem",
                  borderRadius: "0.5rem",
                  position: "relative",
                }}
                key={index}
              >
                <Image
                  src={item?.coverImage?.secureHttpPath}
                  width={500}
                  height={500}
                  alt={item?.name}
                  style={{
                    height: "100%",
                    width: "5rem",
                    objectFit: "cover",
                    borderRadius: "0.5rem",
                  }}
                />
                <Box
                  sx={{
                    marginLeft: "0.5rem",
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "column",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", fontSize: "13px" }}
                    >
                      {item?.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "13px" }}>
                      {item?.subHeading}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        color: "#15741a",
                      }}
                    >
                      {currencyCode}
                      {item?.amount}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "start",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: "gray",
                          textDecoration: "line-through",
                        }}
                      >
                        {currencyCode}
                        {item?.mrp}
                      </Typography>{" "}
                      &nbsp;{" "}
                      <span style={{ color: "#FFBF00", fontWeight: "lighter" }}>
                        |
                      </span>{" "}
                      &nbsp;
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "13px", fontWeight: "bold" }}
                      >
                        <span style={{ fontWeight: 100 }}>Save</span>{" "}
                        {currencyCode}
                        {item?.discount}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    right: "8px",
                    bottom: "8px",
                  }}
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        background: "none",
                        height: "1.7rem",
                      }}
                    >
                      <CircularProgress size="25px" color="success" />
                    </Box>
                  ) : !isClicked ? (
                    <AddIcon
                      fontSize="small"
                      style={{
                        height: "1.8rem",
                        width: "1.8rem",
                        color: "#15741a",
                        background: "white",
                        padding: "4px",
                        borderRadius: "10rem",
                        cursor: "pointer",
                        boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 5px 1px",
                      }}
                      onClick={() => handleAddService(item, index)}
                    />
                  ) : (
                    <CheckIcon
                      fontSize="small"
                      style={{
                        height: "1.8rem",
                        width: "1.8rem",
                        color: "#15741a",
                        background: "white",
                        padding: "4px",
                        borderRadius: "10rem",
                        cursor: "pointer",
                        boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 5px 1px",
                      }}
                      onClick={() => handleRemoveService(item, index)}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
      </Box>
      {isCartAvailable && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            width: "-webkit-fill-available",
          }}
        >
          <Button
            variant="contained"
            color="success"
            sx={{
              margin: "8px auto",
              width: "-webkit-fill-available",
              borderRadius: "50px",
              background: "#15741a",
              padding: "8px 16px",
            }}
            onClick={handleCartNavigate}
          >
            CHECKOUT
          </Button>
        </Box>
      )}
    </>
  );
};
